import {useEffect} from 'react';
import {EditorMobileOnboardingStatus, startOnboarding, toggleOnboardingState} from '@Components/poster-editor/components/editor-mobile-onboarding/editor-mobile-onboarding.reducer';
import {isUserLoggedIn} from '@Libraries/user.library';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {NEW_MOBILE_UX_COOKIE, NEW_MOBILE_UX_HAS_SEEN_VALUE_LIMIT} from '@Components/poster-editor/components/editor-mobile-onboarding/editor-mobile-onboarding.types';
import {isEditorMobileVariant, setAutoSaveOnboardingCookie, showAutoSaveOnBoarding} from '@Components/poster-editor/library/poster-editor-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';

interface EditorMobileOnboardingProps {
  showOnboardingFromConfig?: boolean;
}

export const EDITOR_AUTOSAVE_ONBOARDING_COOKIE = 'pmwasc';
export function useEditorMobileOnboarding({showOnboardingFromConfig = false}: EditorMobileOnboardingProps): void {
  const hasOnboardingCompleted = useAppSelector((state) => {
    return state.editorMobileOnboarding.hasOnboardingCompleted;
  });
  const onboardingStatus = useAppSelector((state) => {
    return state.editorMobileOnboarding.onBoardingStatus;
  });
  const isPosterLoading = useShowContextualSkeletons();
  const dispatch = useAppDispatch();

  useEffect((): void => {
    if (!isEditorMobileVariant()) {
      return;
    }
    if (isPosterLoading) {
      return;
    }
    startEditorMobileOnboarding();
  }, [isPosterLoading]);

  useEffect((): void => {
    if (onboardingStatus === EditorMobileOnboardingStatus.AUTOSAVE) {
      startAutoSaveOnBoarding();
    }
  }, [onboardingStatus]);

  const shouldShowEditorMobileOnboardingForAnonUsers = (): boolean => {
    const value = readCookie(NEW_MOBILE_UX_COOKIE);
    if (!value) {
      return true;
    }

    const hasSeenValue = Number(value);
    return hasSeenValue < NEW_MOBILE_UX_HAS_SEEN_VALUE_LIMIT;
  };

  const doShowMobileUXOnboarding = (): boolean => {
    if (hasOnboardingCompleted) {
      return false;
    }

    if (!isUserLoggedIn()) {
      return shouldShowEditorMobileOnboardingForAnonUsers();
    }

    return showOnboardingFromConfig;
  };

  const startAutoSaveOnBoarding = (): void => {
    if (showAutoSaveOnBoarding()) {
      showMessageGrowl({
        key: 'autosave-onboarding',
        text: window.i18next.t('pwmjs_autosave_onboarding_text_mobile'),
        icon: 'icon-auto-saved',
        type: GROWL_TYPE.NEUTRAL,
        interval: 5000,
        showCloseIcon: false,
      });

      setAutoSaveOnboardingCookie();

      setTimeout((): void => {
        dispatch(toggleOnboardingState(EditorMobileOnboardingStatus.TIMELINE_MOBILE));
      }, 5000);
      return;
    }

    dispatch(toggleOnboardingState(EditorMobileOnboardingStatus.TIMELINE_MOBILE));
  };

  const updateCookieValue = (): void => {
    const value = readCookie(NEW_MOBILE_UX_COOKIE);

    if (!value) {
      setCookie(NEW_MOBILE_UX_COOKIE, '1', 180);
    } else {
      const newValue = Number(value) + 1;
      setCookie(NEW_MOBILE_UX_COOKIE, String(newValue), 180);
    }
  };

  const updateUserHasSeenValue = (): void => {
    if (isUserLoggedIn()) {
      void window.PMW.writeLocal('user/updateUserHasSeenMobileUXOnboarding');
    } else {
      updateCookieValue();
    }
  };

  const startEditorMobileOnboarding = (): void => {
    if (!doShowMobileUXOnboarding()) {
      dispatch(toggleOnboardingState(EditorMobileOnboardingStatus.AUTOSAVE));
      return;
    }

    dispatch(startOnboarding());
    updateUserHasSeenValue();
  };
}
